import type { JSX, ReactNode } from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import { DialogOverlay, DialogPortal } from '../components/ui/dialog.tsx'
import {
  Sheet,
  SheetClose,
  SheetHeader,
  SheetTitle
} from '../components/ui/sheet.tsx'
import { cn } from '../lib/cn.ts'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'

export interface NavSheetProps {
  'data-testid': string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Names the dialog for assistive technology; visually hidden. */
  title: string
  children: ReactNode
  className?: string
  /** Restore focus to an external trigger when the sheet closes. */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * NavSheet — an off-canvas panel for navigation at narrow widths.
 *
 * Wraps shadcn's Sheet so feature code does not import
 * `src/components/ui/` directly, and so the accessible name is not optional:
 * a dialog without one is announced as "dialog" and nothing else.
 *
 * @param props - NavSheet props.
 * @param props.open - Whether the panel is showing.
 * @param props.onOpenChange - Called when the panel opens or closes.
 * @param props.title - Accessible name; visually hidden.
 * @param props.children - Panel content.
 * @param props.className - Extra classes.
 * @returns The rendered panel.
 */
export function NavSheet({
  'data-testid': testId,
  open,
  onOpenChange,
  title,
  children,
  className,
  onCloseAutoFocus
}: NavSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/*
        SheetContent paints its overlay with no class of our own, and that
        overlay is raw black. SheetOverlay is not exported, so the portal is
        composed here. The close control is ours too: the primitive one is
        drawn at 70% opacity.
      */}
      <DialogPortal>
        <DialogOverlay
          data-testid={`${testId}-overlay`}
          className="bg-ink-deep/70"
        />
        <DialogPrimitive.Content
          data-slot="sheet-content"
          data-testid={testId}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            'fixed z-50 flex flex-col gap-4 bg-background transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500',
            'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
            'bg-sidebar text-sidebar-foreground w-64 rounded-none border-(length:--rule-width) border-rule p-0 shadow-none focus-visible:ring-0 [&>button]:focus:ring-0 [&>button]:focus:ring-offset-0',
            className
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          {children}
          <SheetClose asChild>
            <Button
              data-testid={`${testId}-close`}
              variant="quiet"
              size="icon-sm"
              className="absolute top-4 right-4"
            >
              <Icon name="x" />
              <span className="sr-only">{'Close'}</span>
            </Button>
          </SheetClose>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Sheet>
  )
}
